import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Logo, Wordmark } from "../shared/Logo";
import { ThemeToggle } from "../shared/ThemeToggle";
import { ChatWidget } from "./ChatWidget";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";
import { Menu, X, ArrowRight } from "lucide-react";
import { COMPANY } from "../../lib/constants";

const NAV = [
  { to: "/services", label: "Services" },
  { to: "/why-castlecorp", label: "Why CastleCorp" },
  { to: "/service-areas", label: "Service Areas" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const portalPath = user && user.role === "admin" ? "/ops" : "/portal";

  return (
    <div className="min-h-screen bg-background noise-cc">
      <header className="sticky top-0 z-50 glass-cc bg-background/80 border-b">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-2.5 text-foreground">
            <Logo size={26} />
            <Wordmark />
          </Link>
          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-link-${n.to.slice(1)}`}
                className={({ isActive }) =>
                  `text-[13px] font-medium transition-colors duration-200 ${isActive ? "text-[var(--cc-accent)]" : "text-muted-foreground hover:text-foreground"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-1.5 sm:gap-2.5">
            <div className="hidden sm:block"><ThemeToggle /></div>
            {user ? (
              <Button data-testid="nav-portal-button" size="sm" variant="outline" className="rounded-full hidden sm:inline-flex" onClick={() => navigate(portalPath)}>
                {user.role === "admin" ? "CastleOps" : "My Portal"}
              </Button>
            ) : (
              <Button data-testid="nav-login-button" size="sm" variant="ghost" className="hidden sm:inline-flex rounded-full" onClick={() => navigate("/login")}>
                Sign In
              </Button>
            )}
            <Button data-testid="nav-quote-button" size="sm" variant="outline" className="hidden md:inline-flex rounded-full" onClick={() => navigate("/quote")}>
              Request Quote
            </Button>
            <Button data-testid="nav-book-button" size="sm" className="rounded-full btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold px-3 sm:px-4"
              onClick={() => navigate("/book")}>
              <span className="sm:hidden">Book</span><span className="hidden sm:inline">Book Now</span>
              <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
            <button data-testid="nav-mobile-toggle" className="lg:hidden p-2 -mr-1" onClick={() => setOpen(!open)} aria-label="Menu">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {open && (
          <nav className="lg:hidden border-t bg-background px-5 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-sm font-medium text-muted-foreground hover:text-foreground py-1">
                {n.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-3 pt-2 border-t mt-1">
              <ThemeToggle />
              {user ? (
                <Link to={portalPath} onClick={() => setOpen(false)} className="text-sm font-semibold text-[var(--cc-accent)]">
                  {user.role === "admin" ? "Open CastleOps" : "My Portal"}
                </Link>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="text-sm font-semibold text-[var(--cc-accent)]">Sign In</Link>
              )}
              <Link to="/quote" onClick={() => setOpen(false)} className="text-sm font-medium py-1">Request Quote</Link>
            </div>
          </nav>
        )}
      </header>

      <main className="relative z-10"><Outlet /></main>

      <footer className="relative z-10 border-t mt-24 bg-[#0A0A0C] text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 grid grid-cols-2 md:grid-cols-4 gap-10">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2.5"><Logo size={26} /><Wordmark variant="silver" /></div>
            <p className="mt-4 text-sm text-white/50 leading-relaxed">Premium operations. Vetted crews. Precision execution across Southern California.</p>
            <p className="mt-4 overline-cc">{COMPANY.insured}</p>
          </div>
          <div>
            <p className="overline-cc mb-4">Services</p>
            {["Delivery", "Moving", "Furniture Assembly", "Junk Removal", "Labor"].map((s) => (
              <Link key={s} to="/services" className="block text-sm text-white/60 hover:text-white py-1 transition-colors duration-200">{s}</Link>
            ))}
          </div>
          <div>
            <p className="overline-cc mb-4">Company</p>
            {[["About", "/about"], ["Why CastleCorp", "/why-castlecorp"], ["Careers", "/careers"], ["FAQ", "/faq"], ["Contact", "/contact"]].map(([l, to]) => (
              <Link key={to} to={to} className="block text-sm text-white/60 hover:text-white py-1 transition-colors duration-200">{l}</Link>
            ))}
          </div>
          <div>
            <p className="overline-cc mb-4">Operations</p>
            <p className="text-sm text-white/60 py-1">San Diego & Oceanside</p>
            <p className="text-sm text-white/60 py-1">Long Distance to LA & Vegas</p>
            <p className="text-sm text-white/60 py-1">Same-Day Available</p>
            <a href={`mailto:${COMPANY.email}`} className="block text-sm text-[var(--cc-accent)] py-1 mt-2">{COMPANY.email}</a>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-white/40 font-mono-cc">© {new Date().getFullYear()} CASTLECORP — ALL SYSTEMS OPERATIONAL</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-xs text-white/40 hover:text-white/70">Privacy Policy</Link>
              <Link to="/terms" className="text-xs text-white/40 hover:text-white/70">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>
      <ChatWidget />
    </div>
  );
}
